import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SOURCE = 'cart-gift-order';
const GIFT_SOURCE = 'kite';
const REMOVAL_ATTEMPTS = 2;

class CartGiftOrderManager {
  #running = false;
  #pending = false;

  constructor() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source !== GIFT_SOURCE) return;

    const cart = event.detail.resource;
    if (cart?.items && this.#linesToMove(cart).length === 0) return;

    this.#reorder();
  };

  #isFree = (item) => item.final_line_price === 0;

  #isRemoving(item) {
    return Boolean(document.querySelector(`.cart-items__table-row--removing[data-key="${item.key}"]`));
  }

  #sectionIds() {
    const ids = new Set();

    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
    });

    return Array.from(ids).join(',');
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  async #request(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.description || response.statusText);
    return payload;
  }

  #linesToMove(cart) {
    const firstFreeIndex = cart.items.findIndex(this.#isFree);
    if (firstFreeIndex === -1) return [];

    return cart.items
      .slice(firstFreeIndex)
      .filter((item) => !this.#isFree(item) && !item.parent_relationship && !this.#isRemoving(item));
  }

  #toAddLine(item) {
    const properties = { ...item.properties };
    properties._sort = properties._sort === '1' ? '2' : '1';

    const line = { id: item.variant_id, quantity: item.quantity, properties };
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) line.selling_plan = sellingPlanId;

    return line;
  }

  async #removePass(keys, sections) {
    let cart = null;

    for (const key of keys) {
      try {
        cart = await this.#request(Theme.routes.cart_change_url, {
          id: key,
          quantity: 0,
          sections,
          sections_url: window.location.pathname,
        });
      } catch (error) {
        console.error('[cart-gift-order] removing a moved line failed', error);
      }
    }

    return cart;
  }

  async #removeMovedLines(lines) {
    const sections = this.#sectionIds();
    let keys = lines.map((item) => item.key);
    let cart = null;

    for (let attempt = 0; attempt < REMOVAL_ATTEMPTS && keys.length > 0; attempt++) {
      cart = (await this.#removePass(keys, sections)) ?? cart ?? (await this.#fetchCart());
      keys = keys.filter((key) => cart.items.some((item) => item.key === key));
    }

    return cart;
  }

  async #reorder() {
    if (this.#running) {
      this.#pending = true;
      return;
    }

    this.#running = true;
    try {
      const lines = this.#linesToMove(await this.#fetchCart());

      if (lines.length > 0) {
        await this.#request(Theme.routes.cart_add_url, {
          items: lines.map((item) => this.#toAddLine(item)).reverse(),
        });

        const cart = await this.#removeMovedLines(lines);

        document.dispatchEvent(
          new CartUpdateEvent(cart, SOURCE, {
            source: SOURCE,
            itemCount: cart.item_count,
            sections: cart.sections,
          })
        );
      }
    } catch (error) {
      console.error('[cart-gift-order] reorder failed', error);
    } finally {
      this.#running = false;
    }

    if (this.#pending) {
      this.#pending = false;
      this.#reorder();
    }
  }
}

new CartGiftOrderManager();
